import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import FolderItem from './FolderItem';

const CANVAS_W = 2400;
const CANVAS_H = 1600;
const ITEM_W = 108;
const ITEM_H = 140;
const COLS = 6;
const COL_GAP = 60;
const ROW_GAP = 60;
const START_X = 120;
const START_Y = 120;

function autoPosition(index) {
  const col = index % COLS;
  const row = Math.floor(index / COLS);
  return {
    x: START_X + col * (ITEM_W + COL_GAP),
    y: START_Y + row * (ITEM_H + ROW_GAP),
  };
}

export const FolderCanvas = ({
  folders,
  notes,
  updateFolder,
  deleteFolder,
  onOpenFolderTools,
  onEnterFolder,
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);

  // Assign positions to folders that have none, persisted back on first render only
  useEffect(() => {
    folders.forEach((f, i) => {
      if (!f.position) {
        updateFolder(f.id, { position: autoPosition(i) });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleZoom = useCallback((delta) => {
    setZoom(z => Math.min(2.5, Math.max(0.3, z + delta)));
  }, []);

  // Ctrl+wheel zoom, same as Canvas
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        setZoom(z => Math.min(2.5, Math.max(0.3, z + (e.deltaY > 0 ? -0.04 : 0.04))));
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const foldersWithPos = folders.map((f, i) => ({
    ...f,
    position: f.position || autoPosition(i),
  }));

  const noteCountForFolder = useCallback((folderId) =>
    notes.filter(n => n.folderId === folderId).length,
    [notes]
  );

  return (
    <div className="w-full h-full flex flex-col overflow-hidden relative">

      {/* Zoom bar — same position/style as Canvas.jsx */}
      <div
        className="
          fixed top-12 left-1/2 -translate-x-1/2 z-40
          flex items-center gap-1 px-3 py-0.5
          bg-card/95 backdrop-blur-md border border-border border-t-0
          rounded-b-xl shadow-[0_8px_20px_-10px_rgba(0,0,0,0.35)]
        "
      >
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleZoom(-0.1)} disabled={zoom <= 0.3}>
          <ZoomOut className="h-3.5 w-3.5" />
        </Button>
        <span className="text-[11px] font-medium w-10 text-center">{Math.round(zoom * 100)}%</span>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleZoom(0.1)} disabled={zoom >= 2.5}>
          <ZoomIn className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setZoom(1)} disabled={zoom === 1}>
          <RotateCcw className="h-3 w-3" />
        </Button>
      </div>

      {/* Scrollable canvas container */}
      <div
        ref={containerRef}
        className="w-full h-full overflow-auto"
      >
        <div
          style={{
            width: CANVAS_W * zoom,
            height: CANVAS_H * zoom,
            position: 'relative',
          }}
        >
          <div
            ref={canvasRef}
            className="relative bg-gradient-to-br from-background via-primary/5 to-accent/5"
            style={{
              width: CANVAS_W,
              height: CANVAS_H,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              backgroundImage: 'radial-gradient(circle at 2px 2px, hsl(var(--border)) 1px, transparent 0)',
              backgroundSize: '32px 32px',
            }}
          >
          {foldersWithPos.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center space-y-4 animate-fadeIn">
                <div className="text-6xl">📁</div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground/80">No folders yet</h3>
                  <p className="text-muted-foreground">Click + in the toolbar to create a folder</p>
                </div>
              </div>
            </div>
          )}

          {foldersWithPos.map(folder => (
            <FolderItem
              key={folder.id}
              folder={folder}
              updateFolder={updateFolder}
              onOpen={onEnterFolder}
              onOpenFolderTools={onOpenFolderTools}
              zoom={zoom}
              noteCount={noteCountForFolder(folder.id)}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
  );
};

export default FolderCanvas;
